import { Injectable, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';

// Config
import { API_V3_URL } from '../../config/config';

// Models
import { User } from '../../models/user.model';

// RXJS
import { Observable } from 'rxjs';

// Get language
import { LanguageService } from '../language/language.service';
import { MixpanelService } from '../analytics/mixpanel.service';
import { ApiCallBase } from '../api-lab4u/apiCallBase';

/**
 * Component to manage all the user characteristics
 */
@Injectable({
  providedIn: 'root',
})
export class UserSessionService extends ApiCallBase implements OnInit {
  language!: string;
  userId!: string;
  user!: User;
  token!: string;
  message: string = '';
  plan_id: string = '';
  plan_name: string = '';

  /**
   * Following variables declared
   * @param languageService Service that works with the page language
   * @param http Service that allows http request
   * @param router Variable that manage the routes
   */
  constructor(
    public languageService: LanguageService,
    public http: HttpClient,
    public router: Router,
    public mixpanelService: MixpanelService,
  ) {
    super(languageService, http, router);
  }

  ngOnInit() {
    this.loadStorage();
  }

  /**
   * Function that returns only if the user has sign in or not
   */
  isLogin() {
    return this.token.trim().length > 40 ? true : false;
  }

  /**
   * Function that loads the information from the localStorage
   */
  loadStorage() {
    const token = localStorage.getItem('token');
    if (token) {
      this.token = token;
      this.user = JSON.parse(localStorage.getItem('user') ?? 'null') ?? new User();
      this.userId = localStorage.getItem('userId') ?? '';
      this.language = localStorage.getItem('language') ?? '';
      setTimeout(() => {
        this.initializeMixpanelUserProfile(this.user, this.userId);
      }, 0);
    } else {
      this.token = '';
      this.user = new User();
      this.userId = '';
    }
  }

  /**
   * Function that saves the information at the localStorage
   * @param userId Variable that brings the user ID
   * @param token Variable that brings the user token
   * @param user Variable that brings the user data
   */
  saveStorage(userId: string, token: string, user: User): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      try {
        this.token = token;
        this.userId = userId;
        localStorage.setItem('userId', userId);
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('new_user_login', 'true');

        this.infoUser().subscribe({
          next: (resp: any) => {
            const user = resp.response.user;
            localStorage.setItem('user', JSON.stringify(user));
            this.initializeMixpanelUserProfile(user, userId);
            this.user = user;
            observer.next(true);
            observer.complete();
          },
          error: (err) => {
            observer.error(err);
          },
        });
      } catch (error) {
        observer.error(error);
      }
    });
  }

  /**
   * Function to obtain the 100% of the data of the user
   */
  infoUser(userId = '', userToken = '') {
    const url = `${API_V3_URL}/users/${userId || this.userId}`;
    return this.http.get(
      url,
      userToken
        ? this.getHttpOptionsV3WithToken(userToken)
        : this.getHttpOptionsV3(),
    );
  }

  user_role: string = '';
  //* gets the user's role to use in the directive
  getRole() {
    this.user_role = this.user.user_type ?? '';
    return this.user_role;
  }
  user_deal: string = '';
  getInstitution() {
    this.user_deal = this.user.deal ?? '';
    return this.user_deal;
  }

  getId() {
    return this.userId;
  }
  user_email: string = '';
  getUserEmail() {
    this.user_email = this.user.email ?? '';
    return this.user_email.toUpperCase();
  }

  /**
   * Function to initialize the Mixpanel User Profile
   */
  initializeMixpanelUserProfile(user: User, userId: string) {
    this.mixpanelService.init(
      user.email ?? '',
      user.first_name ?? '',
      userId,
      user.last_name ?? '',
      user.usergroup ?? '',
      user.user_type ?? '',
    );
  }
}
